import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Button, Stack, IconButton, Paper, Chip, TextField, Dropdown, DropdownMenuItem} from 'views/components';
import {Edit as EditIcon, BadgeOutlined as BadgeOutlinedIcon} from 'views/icons';
import {MarkdownViewer, MarkdownEditor, Translate, translate} from 'views/module';

const matchDifficultyOptions = ['EASY', 'MEDIUM', 'HARD'];

const CaregiverRequirements = (props) => {
  const {caregiverRequirements, onSave} = props;
  const [editing, setEditing] = React.useState(false);
  const [careplanSummary, setCareplanSummary] = React.useState(caregiverRequirements?.careplanSummary || '');
  const [scheduleRequirement, setscheduleRequirement] = React.useState(caregiverRequirements?.scheduleRequirement || '');
  const [matchDifficulty, setMatchDifficulty] = React.useState(caregiverRequirements?.matchDifficulty || '');
  const [caregiverRequirementChips, setCaregiverRequirementChips] = React.useState(caregiverRequirements?.caregiverRequirementChips || []);
  const [newChipValue, setNewChipValue] = React.useState('');
  const [mouseOver, setMouseOver] = React.useState(false);

  React.useEffect(() => {
    if (editing) return;
    setCareplanSummary(caregiverRequirements?.careplanSummary || '');
    setscheduleRequirement(caregiverRequirements?.scheduleRequirement || '');
    setMatchDifficulty(caregiverRequirements?.matchDifficulty || '');
    setCaregiverRequirementChips(caregiverRequirements?.caregiverRequirementChips || []);
    setNewChipValue('');
  }, [caregiverRequirements, editing]);

  const handleAddChip = () => {
    const trimmed = newChipValue.trim();
    if (!trimmed) return;
    if (caregiverRequirementChips.includes(trimmed)) {
      setNewChipValue('');
      return;
    }
    setCaregiverRequirementChips([...caregiverRequirementChips, trimmed]);
    setNewChipValue('');
  };

  const handleDeleteChip = (chip) => {
    setCaregiverRequirementChips((chips) => chips.filter((c) => c !== chip));
  };

  const handleSave = () => {
    const updatedSection = {
      caregiverRequirements: {
        careplanSummary,
        scheduleRequirement,
        matchDifficulty: matchDifficulty || 'EASY',
        caregiverRequirementChips,
      },
    };
    onSave(updatedSection);
    setEditing(false);
  };

  const handleCancel = () => {
    setCareplanSummary(caregiverRequirements?.careplanSummary || '');
    setscheduleRequirement(caregiverRequirements?.scheduleRequirement || '');
    setMatchDifficulty(caregiverRequirements?.matchDifficulty || '');
    setCaregiverRequirementChips(caregiverRequirements?.caregiverRequirementChips || []);
    setNewChipValue('');
    setEditing(false);
  };

  const renderMatchDifficultyLabel = (value) => {
    const label = translate(`client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficultyOptions.${value}`);
    return label || value;
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
      onMouseOver={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
          justifyContent: 'space-between',
          borderRadius: '4px 4px 0 0',
          width: '100%',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <BadgeOutlinedIcon sx={{mr: 1}} />
          <Typography variant="h6">
            <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.Title" />
          </Typography>
        </Box>
        <IconButton
          sx={{
            visibility: editing ? 'hidden' : 'visible',
            opacity: mouseOver ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          onClick={() => setEditing(true)}
        >
          <EditIcon />
        </IconButton>
      </Box>
      <Box sx={{p: 2}}>
        {
          editing ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.CareplanSummary" />
                </Typography>
                <MarkdownEditor
                  value={careplanSummary}
                  onChange={setCareplanSummary}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.ScheduleRequirement" />
                </Typography>
                <MarkdownEditor
                  value={scheduleRequirement}
                  onChange={setscheduleRequirement}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficulty" />
                </Typography>
                <Dropdown
                  value={matchDifficulty}
                  onChange={setMatchDifficulty}
                  label={translate('client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficulty')}
                >
                  {
                    matchDifficultyOptions.map((option) => (
                      <DropdownMenuItem key={option} value={option}>
                        {renderMatchDifficultyLabel(option)}
                      </DropdownMenuItem>
                    ))
                  }
                </Dropdown>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.RequirementChips" />
                </Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems="flex-start" sx={{mb: 1}}>
                  <TextField
                    label={<Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.RequirementChipPlaceholder" />}
                    value={newChipValue}
                    onChange={setNewChipValue}
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddChip();
                      }
                    }}
                  />
                  <Button variant="contained" onClick={handleAddChip} sx={{whiteSpace: 'nowrap'}}>
                    <Translate expression="generic.Add" />
                  </Button>
                </Stack>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                  {
                    caregiverRequirementChips.length > 0 ?
                      caregiverRequirementChips.map((chip) => (
                        <Chip
                          key={chip}
                          label={chip}
                          onDelete={() => handleDeleteChip(chip)}
                        />
                      )) :
                      (
                        <Typography variant="body2">
                          <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.NoRequirementChips" />
                        </Typography>
                      )
                  }
                </Box>
              </Box>
              <Stack direction="row" justifyContent="flex-end" spacing={1} pt={1}>
                <Button onClick={handleCancel}>
                  <Translate expression="generic.Cancel" />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{minWidth: '100px'}}
                >
                  <Translate expression="generic.Save" />
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.CareplanSummary" />
                </Typography>
                <MarkdownViewer
                  value={careplanSummary || translate('client.ClientDetail.Careplan.CaregiverRequirements.NoCareplanSummary')}
                  truncate={true}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.ScheduleRequirement" />
                </Typography>
                <MarkdownViewer
                  value={scheduleRequirement || translate('client.ClientDetail.Careplan.CaregiverRequirements.NoScheduleRequirement')}
                  truncate={true}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.MatchDifficulty" />
                </Typography>
                <Typography variant="body2">
                  {matchDifficulty ?
                    renderMatchDifficultyLabel(matchDifficulty) :
                    translate('client.ClientDetail.Careplan.CaregiverRequirements.NoMatchDifficulty')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
                  <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.RequirementChips" />
                </Typography>
                {
                  caregiverRequirementChips.length > 0 ? (
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                      {
                        caregiverRequirementChips.map((chip) => (
                          <Chip key={chip} label={chip} />
                        ))
                      }
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      <Translate expression="client.ClientDetail.Careplan.CaregiverRequirements.NoRequirementChips" />
                    </Typography>
                  )
                }
              </Box>
            </Stack>
          )
        }
      </Box>
    </Paper>
  );
};

CaregiverRequirements.propTypes = {
  caregiverRequirements: PropTypes.shape({
    careplanSummary: PropTypes.string,
    scheduleRequirement: PropTypes.string,
    matchDifficulty: PropTypes.oneOf(['EASY', 'MEDIUM', 'HARD']),
    caregiverRequirementChips: PropTypes.arrayOf(PropTypes.string),
  }),
  onSave: PropTypes.func.isRequired,
};

export default CaregiverRequirements;
